<template>
<div class="chapter">
<div class="inner_page">
<div>
<a id="p28"></a>
<h1 class="chapter-title"><span><a href="#/toc#toc-chapter003">Three</a></span></h1>
<p class="noindent"><span class="dropcap">A</span>t the bottom of the deep end of the Centurion Inn swimming pool, Jeremy Cook hated his life. It was Sunday afternoon. He had
         resisted this feeling for two days, but it finally defeated him at an unexpected moment—after a deep dive out of the hot Missouri
         sunshine into the shock of cool water, performed with a run and a bounce and a splash that trumpeted joie de vivre. The sudden
         silence, the pressing weight of water, the hostility of the enveloping medium—these combined to cave him in, depressing him
         so much that it seemed unlikely that his body could rise to the surface. But it did, and he dragged himself sadly out of the
         pool.
     </p>
<p>Motels are venues of negative feelings. Thus it had been a mistake the night before for him to pause and reflect on how lonely
         he was after just one day away from his old milieu, and what this meant for his alternative career plan of splendid isolation
         in northern Wisconsin. It had also been a mistake that morning to check—again—in the motel office for the package of promised
         literature from the Pillow Agency (“Nothing yet, sir”), and to ask if anyone had indicated they would be paying for his room
         (“We were rather hoping you would do that, sir”).
     </p>
<p>And it had been a mistake to look at the women staying at <a id="p29"></a>the motel, not because the pretty ones worked him into a lather (he had long ago resigned himself to that futile exercise),
         but because they all reminded him of Paula. More than that, he thought they <em>were</em> Paula. Women who bore only the faintest resemblance to her fooled him into thinking she was <em>here</em>, in this hateful new environment.
     </p>
<p>And it was a mistake now, after his episode of aquatic depression, to lie on the poolside lounge chair, squinting against
         the bright, hot sky, and think about how “strange and difficult” he was—Paula’s characterization of him during their worst
         fight. She had enumerated his problems for him. Although he saw social isolation as a kind of failure, he was alone almost
         all the time. He liked to talk about arcane subjects but was irritated and threatened when others did so. He took contradiction
         personally yet never intended <em>his</em> contradictions of others to be taken that way. Strange and difficult.
     </p>
<p>Jets droned in and out of the airport across 1-70, which competed for supremacy in this asphalted hell with its own din. Cook
         groaned and rose from his chair. He gathered his newspaper, swaying a bit with dizziness. As he walked to his room, a plane
         came in low enough to strafe him. Its aluminum underbelly matched the strange metallic taste in his mouth.
     </p>
<p>His room was an igloo. He had turned up the air conditioner before going out to the pool, but he hadn’t expected such a zealous
         performance. He shivered and went to the controls. A lid covered them. Gentle pressure on one side had easily opened it earlier
         but failed to work now. He pressed the lid several times, on every side, on every square inch. He pounded it with his fist.
         He threw his towel at it. Last of all—though least effective—he made an obscene gesture at it by grabbing his genitals in
         a look-what-I-got fashion.
     </p>
<p>He turned and lay facedown on the bed in his wet swimming suit, freezing but overcome with fatigue. He decided he was sick—or
         “ill,” as some people said. Why did they say <a id="p30"></a>that? With that angry question, he drifted off.
     </p>
<p>He slept. He shivered. He moaned. In two hours he awoke twenty times, always with the same thought: got to get to those controls.
         The same thought every time, with the same image of the lid yielding easily to him and his hand reaching in and turning the
         thermostat dial. At some point he managed to crawl under the covers. Then he slept soundly, disturbed only once when the maid
         came in with the truly foolish hope of cleaning his room. He sat up wildly in bed and spoke to her, mainly in vowels, from
         deep within his fever delirium. There had been a meeting of minds, for she had backed out quickly and not returned.
     </p>
<p>When he awoke again, night had fallen. His room was dark. The air conditioner pumped away as if powered by its own direct
         pipeline to Saudi Arabia. He groped for the light switch by the bed, then bent close over his watch and squinted at it. Two-fifteen.
     </p>
<p>Excellent! He flopped back on the bed. A <em>perfect</em> time to be wide-awake. Wasn’t this the “shank of the night” that Bing Crosby had sung about? No?
     </p>
<p>He got up and went to the window.</p>
<p>Missouri slept.</p>
<p>He was utterly alone.</p>
<p class="cotx">Roy Pillow hid whatever he felt when Cook appeared at his office door Monday morning. Pillow’s bald head was free of wrinkles,
         as was his forehead. He rose from behind his desk. In the small office he seemed even taller than Cook remembered him.
     </p>
<p>“You owe me one hundred and sixty-two dollars and thirty-eight cents,” Cook said.</p>
<p>The wrinkles appeared as if Cook had plastered them there with a shot from a pistol. “Pardon?” said Pillow.</p>
<p>“My room bill. I’ve got the receipt right here.” Cook took it from his coat pocket and waved it in the air dramatically.</p>
<a id="p31"></a>
<p>Pillow stared at him a moment. Then he gestured to a chair across from his desk. “Sit down.”</p>
<p>“No. I don’t plan to stay.”</p>
<p>“How’s that?” said Pillow. There went the head, cocked to one side. Pretty soon he would say “Oh come now.”</p>
<p>“Just write me a check and I’ll be on my way,” said Cook.</p>
<p>“But we agreed that if you came here today, you would become one of the staff.”</p>
<p>“I’m here, yes, but for a different reason.”</p>
<p>“But that’s impossible.”</p>
<p>“What?”</p>
<p>“Our agreement was—”</p>
<p>“Our agreement was that you would pay for the goddamned room. And that you would send me some literature about this place.”</p>
<p>“Sit down,” Pillow said in a fresh way, as if starting all over.</p>
<p>“No. Just write me the check.”</p>
<p>“I assure you that I made arrangements for both of those things to be taken care of.”</p>
<p>Cook laughed. “You and your arrangements. Now that I think of it, give me cash. No check. Cash.”</p>
<p>“Are you telling me you had to pay for the room yourself?”</p>
<p>“What do you think?”</p>
<p>“Why didn’t you tell the clerk that <em>I</em> would pay?”
     </p>
<p>“I did!” This was a lie. Cook had asked the clerk about it, but he hadn’t actually told him what to do.</p>
<p>“You instructed him that the Pillow Agency was to be billed?”</p>
<p>Cook couldn’t keep the lie alive. “I assumed <em>you</em> would do that. Besides, what about the literature you promised me?”
     </p>
<p>“That’s a separate matter entirely.” Pillow said this with a strong rebuke in his voice, as if Cook had made an unfair move.</p>
<p>“But what about it? You’re not going to say I should have taken the initiative there, are you?”</p>
<a id="p32"></a>
<p>If Pillow was tempted by this, he hid it well. “Of course not. I’m distressed to hear you didn’t receive anything. I must
         take it up with my staff.”
     </p>
<p>Cook laughed. It was his helpless, impatient laugh. He recognized it from his first talk with Pillow. This talk was just like
         that one. Only the words were different.
     </p>
<p>“Okay,” said Cook. “Okay. You’re doing real well. Point one: the bill for the room—my fault. Point two: the literature—your
         staff’s fault. But there’s point three: your reaction when I told you about the article that criticized my work. Think how
         that made me feel!”
     </p>
<p>By God, thought Cook. He’d hit home. Pillow was silent. He turned and looked out the window. Cook watched him from the chair
         across the desk, which he had taken without really planning to. Pillow seemed to be chewing furiously on the inside of his
         lower lip.
     </p>
<p>Pillow’s phone rang. He grabbed the receiver, lifted it an inch, and immediately let it drop back into its cradle. The phone
         rang again, and Pillow looked at it as if surprised. This time he simply let it go on ringing. He said to Cook, “I would like
         you to meet the rest of the staff.”
     </p>
<p>Cook shouted over the ringing, “What do you mean? In lieu of payment?”</p>
<p>Pillow seemed to study this idea. “That makes no sense.”</p>
<p>“Exactly. One sixty-two thirty-eight makes sense.”</p>
<p>Pillow nodded. “I can see you’re in need of a gesture of good faith.”</p>
<p>“Yes,” said Cook. The phone was driving him crazy. “That’s one way to put it.”</p>
<p>Pillow stood up. “Come. Let’s meet the rest of the staff.”</p>
<p>It was really quite remarkable, Cook thought, how Pillow could state the prelude to an act without delivering on the act itself.
         The phone stopped ringing, and in the sudden calm Cook found himself, for lack of a better plan, rising and following Pillow
         out the door to the adjacent office, room 1202. Pillow knocked on the door, then tried the <a id="p33"></a>knob. It was locked. He repeated the same actions for rooms 1203 through 1209. Nodding as if this was exactly what he had
         expected, Pillow led Cook back up the hall. A young man emerged from the doorway leading to the elevators. He wore a dark
         suit and was highly groomed.
     </p>
<p>“Ah,” said Pillow. “We’re in luck. James Talbot, meet Jeremy Cook.”</p>
<p>Cook shook hands with the man, wincing under his grip. Talbot, having displayed himself, gave Cook a departing smile and hurried
         on down the hall.
     </p>
<p>“And here’s Matthew,” said Pillow, for another one had appeared. “Matthew Benton, say hello to Jeremy Cook.”</p>
<p>Benton was strikingly good-looking, and, like Talbot, was dressed for power. He boomed, “How do you do,” but Cook beat him
         to the strong grip and evened the score. Benton then moved hastily on. Everyone seemed to be pressed by some sort of deadline.
     </p>
<p>“What are those two working on?”</p>
<p>“Mmm?” said Pillow.</p>
<p>“What are they working on? What kind of project?”</p>
<p>“I’m not sure right now.”</p>
<p>Cook was impressed. He liked administrators who gave free rein. “You haven’t assigned them something?”</p>
<p>“No.”</p>
<p>“Don’t they at least report to you?”</p>
<p>“Why would they do that?”</p>
<p>“Because you’re the boss!” Freedom was one thing, he thought. Anarchy was another.</p>
<p>Pillow laughed. “I get you now. You think those two work for me. No no no. They’re lawyers. They have offices up here.”</p>
<p>Cook stared hard at Pillow. “You tricked me. You used those guys to impress me because they’re so normal.” Unlike you, he
         thought.
     </p>
<p>Pillow’s face, showing a flexibility Cook hadn’t suspected, raced into a mournful expression. “You certainly <a id="p34"></a>have a suspicious mind, Jeremy,” he said.
     </p>
<p>Cook felt another helpless laugh coming, but he sent it away angrily.</p>
<p>“We’ve had a nice little walk,” said Pillow. “Let’s return to my office.”</p>
<p>Cook followed, amazed at Pillow, amazed at himself. If Pillow walked out his twelfth-floor window, Cook would probably follow,
         asking for clarification all the way down.
     </p>
<p>“Jeremy,” said Pillow as the two of them sat down, “how much do you know about the Pillow Agency?”</p>
<p>“Well, on Friday I told you I knew very little. Since then I’ve read nothing about it, learned nothing about it, and met two
         guys who have nothing to do with it.”
     </p>
<p>“Mmm,” Pillow mmmed. “I’d like to give you three choices.” He took some manila folders out of a wooden file box on his desk
         and looked at the tabs on them. He shuffled them around a bit and said, “The Wingbermuehles, the Oberniederlanders, and the
         Wilsons. Take your pick.” He looked at Cook.
     </p>
<p>“What are they? Antique airplanes?”</p>
<p>Pillow seemed confused by this. “Take your pick.”</p>
<p>“All right. What the hell, eh? I’ll take the Wilsons. You bet. Give me the Wilsons every time. They’re for me.”</p>
<p>“Mmm.” Pillow returned two of the files to the box and opened the one before him. “A good choice. A charming pair, I’m sure.”</p>
<p>“You know them?”</p>
<p>“Not personally, no.”</p>
<p>“But you know <em>of</em> them. You have a file on them.”
     </p>
<p>Pillow looked sharply at Cook. “This is not for your eyes.”</p>
<p>“I didn’t say it was. I … What would I <em>do</em> with the Wilsons?”
     </p>
<p>“<em>The Pillow Manual</em> will be your guide,” said Pillow as he failed to produce one. Cook told himself not to leave the building without a copy
         of that manual, even if it meant taking hostages.
     </p>
<a id="p35"></a>
<p>Pillow copied down some information that was in the folder. Then he closed the folder and set it to one side of his desk.
         He opened the top drawer, took out something like a large receipt book, and bent over and began to write in it. Cook bided
         his time.
     </p>
<p>Without lifting his head, Pillow said, “Brothers or sisters?”</p>
<p>“Pardon?” said Cook, even though he had heard him.</p>
<p>“Do you have any brothers or sisters?”</p>
<p>Cook looked at Pillow’s bald dome for clues. “I have an older sister in Chicago.” With a cottage in Wisconsin, he thought.</p>
<p>“Mmm.” Pillow set his pen down and slowly sat up straight in his chair. He drummed his fingers on his desk for a moment. “Jeremy,
         let me ask you something. Why does a marriage break down? Why does it crack up?”
     </p>
<p>Cook squirmed. He had never in his life given this question a single moment of thought. “For lots of reasons, I suppose.”</p>
<p>“For <em>one</em> reason.” Pillow blinked slowly, heavily. With stony reverence, he said, “Communication.”
     </p>
<p>“I take it you’re single?” Cook said.</p>
<p>“Not at all! Happily married. Going on six months now.”</p>
<p>“First marriage?” said Cook, surprised. Pillow appeared to be in his fifties.</p>
<p>Pillow became animated. “In a sense, yes. I’m glad you put it that way. I’ll think of it in those terms myself from now on.”
         He leaned forward on his elbows and said earnestly, “You see, Jeremy, I believe in love. It’s a new belief for me, and it’s
         taken the Pillow Agency in a surprising new direction. The Pillow Agency serves marriages. Our specialty is the linguistically
         troubled marriage. That’s where our linguists roll up their sleeves and go to work. They <em>occupy</em> the marriage. The Wilsons are linguistically troubled. You, Jeremy, will <em>occupy</em> their marriage. You will <em>bivouac</em>, so to speak, on their figurative <em>marital battlefield</em>.”
     </p>
<a id="p36"></a>
<p>“Jesus Christ!” said Cook. “I’d rather captain an Iranian speedboat.”</p>
<p>Pillow said, “It will indeed be a challenge. Now for the details. Where are your earthly goods?”</p>
<p>“In Indiana. In storage. I didn’t want to move them until I knew—”</p>
<p>“Fine, fine. Leave them there. You’ll be staying with the Wilsons.”</p>
<p>“You mean I’m supposed to sleep there?”</p>
<p>“Yes. Don’t worry. They’ll provide you with a comfortable bed. It’s all in the contract.”</p>
<p>“It’s not the bed I’m worried about. It’s the whole idea. How long will I be there?”</p>
<p>“As long as you are needed.”</p>
<p>“What will I <em>do?</em>”
     </p>
<p>“<em>The Pillow Manual</em> will be your guide.”
     </p>
<p>“Ah. Silly of me to have forgotten that.”</p>
<p>“You’ll do fine, Jeremy. You’re eminently qualified.”</p>
<p>“I am? My doctoral dissertation was on Miwok syntax. Since then I’ve been studying two-year-old Hoosiers.”</p>
<p>“Exactly. I’m hiring your mind—what you <em>can</em> do, not what you’ve done. Now here are two checks for you. One is for your room bill. The other is for three thousand dollars—a
         small advance on your salary until we’ve gotten the paperwork on you completed.” Pillow stood up.
     </p>
<p>Cook saw the cottage in Wisconsin. It was quaint, but it was cold. A moose stuck his head in the window. Amazing! But the
         moose looked stupid. As for Cook himself, in this vision he sat alone at a bare wooden table, unshaven, maybe even drunk.
         He was trying to speak to the moose, but words failed him: he had spoken to no one in months and his language was gone.
     </p>
<p>Cook rose to his feet and took the two checks.</p>
<p>“And here is the address of the Wilsons,” Pillow said. He suddenly beamed at Cook. “What did the rich man say in that old
         TV show? ‘Our next millionaire, Mike’?” Pillow <a id="p37"></a>chuckled. It was not a pretty sight. “If a happy marriage is money in the bank, then I say to you, ‘Our next millionaire,
         Jeremy.’”
     </p>
<p class="cotx">Cook spent the rest of the day in a tiny conference room on the twelfth floor, studying <em>The Pillow Manual</em> and filling out a long questionnaire Pillow claimed to need for his files. Pillow had escorted him to the room with crazed
         delight in his new employee, slapping Cook on the back and steering him around corners by the elbow. Cook had many questions
         about the agency, and Pillow answered some of them, but once Cook was in the conference room Pillow seemed in a hurry to get
         back to his other business, whatever it might be. As he left Cook, he jabbed his finger at the thick volume under Cook’s arm
         and said, “<em>The Pillow Manual</em> will be your guide.”
     </p>
<p>Pillow had intimated that he and Cook would eat lunch together around noon, but when Cook walked down to his office he found
         the door locked. Cook left a convivial note on the door that read, “Roy, I’m in 1242. Lunch?” Of course, Pillow <em>knew</em> Cook was in 1242—he had taken him there—but that made Cook’s note all the more convivial, according to the principle that
         required a zero-information flow for conviviality. At two o’clock, irritated and starving, Cook gave up on Pillow and wandered
         downstairs and outside to a place with Spartan offerings—two types of chili, one kind of soda, and sit down and shut up and
         eat it. Cook never really fussed over food himself—he rated it for pleasure with gassing up his car—so he didn’t mind the
         place much. Then it was back to <em>The Pillow Manual</em>.
     </p>
<p>Shortly before five o’clock, Cook had a visitor. It was the stern woman who had taken his dictation on Friday. Instead of
         a business suit she wore a white smock, and instead of a pen in her hand she carried a syringe with a long needle. Cook’s
         eyes widened.
     </p>
<a id="p38"></a>
<p>“Blood,” she said succinctly. “Roll up your sleeve.”</p>
<p>“What for?” said Cook, but to his amazement he complied.</p>
<p>She pawed at his arm and, to put him at ease, said, “Let’s see if I can get it right this time.” It hurt, but she managed
         to avoid hitting a nerve and crippling him for life. Cook had a dozen questions, but her manner commanded silence. She was
         gone, and so were several milliliters that used to be part of him, and he didn’t know why.
     </p>
<p>He took a tissue from his pocket and wedged it against the puncture wound, holding it in place by flexing his arm. Working
         clumsily but hurriedly—for all he knew, the woman might return and wrest a stool sample from him—he packed up and set out
         for his assignment.
     </p>
<p>According to Pillow, the Wilsons were expecting him for dinner. They lived about a half mile west of the city limits, in a
         good-looking neighborhood of old brick houses—turn-of-the-century, Cook guessed. He located the house but drove by it without
         stopping. He cruised the neighborhood, telling himself he was researching the Wilsons’ environment instead of stalling. The
         area was thick with oaks and sycamores, almost overpopulated by them. The streets horse-shoed around confusingly, and Cook
         got lost twice. When he tracked down the Wilsons’ house for the third time he was actually glad to do so, and riding on that
         feeling, he pulled to a stop in front of it. He got out and took his suitcase and briefcase from the car.
     </p>
<p>As he walked up the sidewalk to the front door, he felt like many things: an unwelcome relative, an IRS auditor, a schizophrenic
         tuck-pointer back to patch up a sloppy job, a fraud, and a pervert.
     </p>
<p>He pushed the doorbell. He figured he had about seven seconds to begin to feel like what his manual told him he was: a Pillow
         agent.
     </p>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// chapter003.vue?3466df1a